import { useState, useMemo } from 'react'
import { format } from 'date-fns'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { TrendingUp, Users, DollarSign, Receipt, AlertCircle, Lock, LockOpen } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import {
  usePOSOrders,
  useEndOfDayReport,
  usePOSDaySession,
  useClosePOSDay,
  useReopenPOSDay,
} from '@/lib/queries/usePOS'
import { formatPOSMoney, usePOSCurrency } from '@/lib/pos/currency'
import type { POSOrder, POSOrderStatus } from '@/lib/types/pos'

type ReportTab = 'history' | 'analytics'

const TODAY = format(new Date(), 'yyyy-MM-dd')

function orderTotal(order: POSOrder) {
  return (order.orderItems ?? [])
    .filter((i) => i.status !== 'voided')
    .reduce((s, i) => s + i.unitPrice * i.quantity, 0)
}

const STATUS_STYLE: Record<POSOrderStatus, { label: string; color: string }> = {
  open:           { label: 'Open',       color: 'bg-green-500/10 text-green-400' },
  sent:           { label: 'In Kitchen', color: 'bg-amber-500/10 text-amber-400' },
  partially_paid: { label: 'Part. Paid', color: 'bg-orange-500/10 text-orange-400' },
  paid:           { label: 'Paid',       color: 'bg-blue-500/10 text-blue-400' },
  voided:         { label: 'Voided',     color: 'bg-zinc-500/10 text-[var(--color-text-muted)]' },
  closed:         { label: 'Closed',     color: 'bg-zinc-500/10 text-[var(--color-text-muted)]' },
}

// ── History tab ───────────────────────────────────────────────────────────────

function HistoryTab() {
  const [date, setDate] = useState(TODAY)
  const currency = usePOSCurrency()
  const money = useMemo(() => (amount: number) => formatPOSMoney(amount, currency), [currency])
  const isToday = date === TODAY

  const { data: ordersResp, isLoading } = usePOSOrders(date, { refetchInterval: isToday ? 30_000 : 0 })
  const { data: sessionResp } = usePOSDaySession(TODAY)
  const closeDay  = useClosePOSDay()
  const reopenDay = useReopenPOSDay()

  const orders: POSOrder[] = (ordersResp as any)?.data ?? []
  const session  = (sessionResp as any)?.data
  const isClosed = session?.status === 'closed'

  const sorted = useMemo(
    () => [...orders].sort((a, b) => (b.openedAt > a.openedAt ? 1 : -1)),
    [orders],
  )

  return (
    <div className="h-full space-y-4 overflow-y-auto pr-1">
      {/* Controls row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-[var(--color-text-secondary)]">Date</label>
          <input
            type="date"
            value={date}
            max={TODAY}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-1.5 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent-amber)]"
          />
          {!isToday && (
            <button
              onClick={() => setDate(TODAY)}
              className="px-2.5 py-1.5 text-xs font-medium rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              Today
            </button>
          )}
        </div>

        {/* End Day — only when viewing today */}
        {isToday && (
          <div className={[
            'flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2',
            isClosed ? 'bg-zinc-500/5 border-zinc-500/20' : 'bg-green-500/5 border-green-500/20',
          ].join(' ')}>
            <div className="flex items-center gap-1.5">
              {isClosed
                ? <Lock className="h-3.5 w-3.5 text-zinc-400" />
                : <LockOpen className="h-3.5 w-3.5 text-green-400" />}
              <span className={`text-xs font-medium ${isClosed ? 'text-zinc-400' : 'text-green-400'}`}>
                {isClosed
                  ? `Day closed${session?.closedAt ? ` at ${format(new Date(session.closedAt), 'HH:mm')}` : ''}`
                  : 'Day open'}
              </span>
            </div>
            {isClosed ? (
              <Button variant="secondary" size="sm" onClick={() => reopenDay.mutate(TODAY)} disabled={reopenDay.isPending}>
                Reopen
              </Button>
            ) : (
              <Button size="sm" onClick={() => closeDay.mutate(TODAY)} disabled={closeDay.isPending}>
                End Day
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Orders table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={56} />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] py-12 justify-center">
          <AlertCircle className="h-4 w-4" />
          No orders for this date.
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-subtle)]">
                  <th className="text-left px-4 py-2 text-[var(--color-text-muted)] font-medium">Time</th>
                  <th className="text-left px-4 py-2 text-[var(--color-text-muted)] font-medium">Table</th>
                  <th className="text-right px-4 py-2 text-[var(--color-text-muted)] font-medium">Covers</th>
                  <th className="text-right px-4 py-2 text-[var(--color-text-muted)] font-medium">Items</th>
                  <th className="text-right px-4 py-2 text-[var(--color-text-muted)] font-medium">Total</th>
                  <th className="text-right px-4 py-2 text-[var(--color-text-muted)] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((order) => {
                  const s = STATUS_STYLE[order.status]
                  const total = orderTotal(order)
                  const itemCount = (order.orderItems ?? []).filter((i) => i.status !== 'voided').length
                  return (
                    <tr key={order.documentId} className="border-b border-[var(--color-border-subtle)] last:border-0 hover:bg-[var(--color-bg-elevated)]/50">
                      <td className="px-4 py-3 text-[var(--color-text-secondary)] tabular-nums">
                        {format(new Date(order.openedAt), 'HH:mm')}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-text-primary)] font-medium">
                        {order.table?.name ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-[var(--color-text-secondary)]">
                        {order.covers ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-[var(--color-text-secondary)]">
                        {itemCount}
                      </td>
                      <td className="px-4 py-3 text-right text-[var(--color-text-primary)] font-medium tabular-nums">
                        {total > 0 ? money(total) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${s.color}`}>
                          {s.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Analytics tab ─────────────────────────────────────────────────────────────

function KpiCard({ label, value, icon: Icon, color }: { label: string; value: string; icon: React.ElementType; color: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-1">{label}</p>
            <p className="text-xl font-semibold text-[var(--color-text-primary)]">{value}</p>
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function AnalyticsTab() {
  const currency = usePOSCurrency()
  const money = useMemo(() => (amount: number) => formatPOSMoney(amount, currency), [currency])
  const { data: eodResp, isLoading } = useEndOfDayReport(TODAY, true)
  const eod = (eodResp as any)?.data

  const tenderChartData = eod
    ? Object.entries(eod.byTenderType as Record<string, number>)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ tender: k.replace('_', ' '), revenue: v }))
    : []

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rectangular" height={80} />)}
      </div>
    )
  }

  if (!eod) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] py-12 justify-center">
        <AlertCircle className="h-4 w-4" />
        No sales data yet today.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Orders"        value={String(eod.totalOrders)}      icon={Receipt}    color="bg-blue-500/10 text-blue-400" />
        <KpiCard label="Covers"        value={String(eod.totalCovers)}      icon={Users}      color="bg-purple-500/10 text-purple-400" />
        <KpiCard label="Gross Revenue" value={money(eod.grossRevenue)}  icon={DollarSign} color="bg-green-500/10 text-green-400" />
        <KpiCard label="Tax Collected" value={money(eod.taxCollected)}  icon={TrendingUp} color="bg-amber-500/10 text-amber-400" />
        <KpiCard label="Tips"          value={money(eod.totalTips)}     icon={DollarSign} color="bg-teal-500/10 text-teal-400" />
        <KpiCard label="Net Revenue"   value={money(eod.netRevenue)}    icon={TrendingUp} color="bg-green-500/10 text-green-400" />
      </div>

      {eod.voidedOrders > 0 && (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] p-3 flex items-center justify-between text-sm">
          <span className="text-[var(--color-text-muted)]">Voided orders</span>
          <span className="font-medium text-[var(--color-text-primary)]">{eod.voidedOrders} ({money(eod.voidedValue)})</span>
        </div>
      )}

      {tenderChartData.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Revenue by Tender</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tenderChartData}>
                  <XAxis dataKey="tender" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-subtle)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', fontSize: 12 }}
                    // @ts-expect-error Recharts may pass undefined values even when the series is numeric.
                    formatter={(v: number) => [money(v), 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="var(--color-accent-amber)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {eod.topItems?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Top Selling Items</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-subtle)]">
                  <th className="text-left px-4 py-2 text-[var(--color-text-muted)] font-medium">#</th>
                  <th className="text-left px-4 py-2 text-[var(--color-text-muted)] font-medium">Item</th>
                  <th className="text-right px-4 py-2 text-[var(--color-text-muted)] font-medium">Qty Sold</th>
                  <th className="text-right px-4 py-2 text-[var(--color-text-muted)] font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {eod.topItems.map((item: any, idx: number) => (
                  <tr key={item.menuItemId} className="border-b border-[var(--color-border-subtle)] last:border-0">
                    <td className="px-4 py-2 text-[var(--color-text-muted)] text-xs font-bold w-8">{idx + 1}</td>
                    <td className="px-4 py-2 text-[var(--color-text-primary)]">{item.name}</td>
                    <td className="px-4 py-2 text-right text-[var(--color-text-secondary)]">{item.qty}</td>
                    <td className="px-4 py-2 text-right text-[var(--color-text-secondary)]">{money(item.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────

export function POSReportsView() {
  const [tab, setTab] = useState<ReportTab>('history')

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1">
        {(['history', 'analytics'] as ReportTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              'px-3 py-1.5 rounded-[var(--radius-sm)] text-sm font-medium transition-colors',
              tab === t
                ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]',
            ].join(' ')}
          >
            {t === 'history' ? 'History' : 'Analytics'}
          </button>
        ))}
      </div>

      {tab === 'history' ? <HistoryTab /> : <AnalyticsTab />}
    </div>
  )
}
